import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Group, IconButton, iconSizes, Stack, Text, Title } from '@common/ui'
import { IconPlus } from '@common/ui/icons'
import { com } from '@eidu/web'
import useEntityTypes from '../../io/useEntityTypes'
import useEntityType from '../../io/useEntityType'
import TanStackDataGrid from '../../components/data/TanStackDataGrid'
import EntityTypeNotAccessibleError from './EntityTypeNotAccessibleError'
import { useImpersonation } from '../../io/context/ImpersonationContext'
import { getKnownKindKey } from '../../domain/kindRegistry'
import BatchPublisherWrapper from '../../ui/microfrontend/BatchPublisherWrapper'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import entityTypeIdFromString = com.eidu.entity.type.entityTypeIdFromString

const isLearningUnitType = (entityType: EntityType): boolean =>
  entityType.kind != null && getKnownKindKey(entityType.kind) === 'LearningUnit'

type ListPageContentProps = {
  onImport: () => void
  onNew: () => void
  entityType: EntityType
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
  pageSize: number
}

export const ListPageContent = ({ onImport, onNew, entityType, entityTypes, pageSize }: ListPageContentProps) => {
  const navigate = useNavigate()
  const { isReadOnly } = useImpersonation()
  const writesAllowed = !isReadOnly
  const showBatchPublish = isLearningUnitType(entityType)

  const [selectedEntities, setSelectedEntities] = useState<readonly EntityWithLabelAndRelated[]>([])

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        padding: 'var(--trellis-spacing-lg)',
        overflow: 'hidden',
      }}
    >
      {writesAllowed && (
        <IconButton
          data-testid="entity-list-create-button"
          variant="filled"
          size="xl"
          radius="xl"
          aria-label={`Create ${entityType.name}`}
          onClick={onNew}
          style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 10 }}
        >
          <IconPlus size={iconSizes.md} stroke={1.5} />
        </IconButton>
      )}
      {entityType && (
        <Group gap="xs" justify="space-between" style={{ flexShrink: 0 }}>
          <Title order={2} data-testid="entity-list-title">
            {entityType.name}
          </Title>
          {writesAllowed && (
            <Group gap="xs" justify="flex-end">
              {showBatchPublish && <BatchPublisherWrapper selectedEntities={selectedEntities} />}
              <Button variant="subtle" data-testid="entity-list-import-button" onClick={onImport}>
                Import
              </Button>
            </Group>
          )}
        </Group>
      )}
      {entityType && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', paddingTop: 'var(--trellis-spacing-xs)' }}>
          <TanStackDataGrid
            navigate={navigate}
            entityTypes={entityTypes}
            entityType={entityType}
            pageSize={pageSize}
            onSelectionChange={showBatchPublish ? setSelectedEntities : undefined}
          />
        </div>
      )}
    </div>
  )
}

export type ReadyListPageProps = {
  entityType: EntityType
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
}

const ReadyListPage = ({ entityType, entityTypes }: ReadyListPageProps) => {
  const navigate = useNavigate()

  return (
    <Stack gap={0} style={{ flex: 1, minHeight: 0 }}>
      <ListPageContent
        onImport={() => {
          navigate('import')
        }}
        onNew={() => {
          navigate('new')
        }}
        entityType={entityType}
        entityTypes={entityTypes}
        pageSize={50}
      />
    </Stack>
  )
}

export type ListPageProps = {
  entityTypeId: EntityTypeId
}

const ListPage = ({ entityTypeId }: ListPageProps) => {
  const { entityTypes, isLoading: typesLoading, error: typesError } = useEntityTypes()
  const { entityType, isLoading: typeLoading, error: typeError } = useEntityType(entityTypeId)

  const error = typesError ?? typeError
  const loading = typesLoading || typeLoading
  const loadingOverlayOpen = loading && !error

  return (
    <>
      {entityType && entityTypes && (
        <ReadyListPage key={entityType.id.toString()} entityType={entityType} entityTypes={entityTypes} />
      )}
      {(!entityType || !entityTypes) && !loadingOverlayOpen && <EntityTypeNotAccessibleError />}
    </>
  )
}

const ListPageRoute = () => {
  const params = useParams()

  if (params.entityTypeId) return <ListPage entityTypeId={entityTypeIdFromString(params.entityTypeId)} />
  else return <Text p="lg">Unknown state</Text>
}

export default ListPageRoute
